import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface BrowserSupport {
  isSupported: boolean;
  browser: string;
  version: string;
  reason: string | null;
}

interface DeviceInfo {
  browser: string;
  version: string;
  os: string;
  isMobile: boolean;
  isPWA: boolean;
}

interface FCMState {
  token: string | null;
  isInitialized: boolean;
  lastMessage: any;
  // ✅ NEW: Token refresh tracking
  lastTokenRefresh: string | null;
  tokenRefreshCount: number;
  previousToken: string | null; // Track old token for backend update
  // ✅ NEW: Browser support detection
  browserSupport: BrowserSupport | null;
  // ✅ NEW: Device information
  deviceInfo: DeviceInfo | null;
}

const initialState: FCMState = {
  token: null,
  isInitialized: false,
  lastMessage: null,
  lastTokenRefresh: null,
  tokenRefreshCount: 0,
  previousToken: null,
  browserSupport: null,
  deviceInfo: null,
};

const fcmSlice = createSlice({
  name: 'fcm',
  initialState,
  reducers: {
    setFCMToken: (state, action: PayloadAction<string | null>) => {
      // Store previous token before updating (for backend token update)
      if (state.token && action.payload && state.token !== action.payload) {
        state.previousToken = state.token;
      }
      state.token = action.payload;
    },
    setInitialized: (state, action: PayloadAction<boolean>) => {
      state.isInitialized = action.payload;
    },
    setLastMessage: (state, action: PayloadAction<any>) => {
      state.lastMessage = action.payload;
    },
    // ✅ NEW: Token refresh actions
    setTokenRefreshed: (state, action: PayloadAction<{ token: string; timestamp: string }>) => {
      state.previousToken = state.token;
      state.token = action.payload.token;
      state.lastTokenRefresh = action.payload.timestamp;
      state.tokenRefreshCount += 1;
    },
    // ✅ NEW: Browser support actions
    setBrowserSupport: (state, action: PayloadAction<BrowserSupport>) => {
      state.browserSupport = action.payload;
    },
    // ✅ NEW: Device info actions
    setDeviceInfo: (state, action: PayloadAction<DeviceInfo>) => {
      state.deviceInfo = action.payload;
    },
    clearFCM: (state) => {
      state.token = null;
      state.isInitialized = false;
      state.lastMessage = null;
      state.previousToken = null;
      // Don't clear browser support and device info as they're environment-specific
    },
  },
});

export const {
  setFCMToken,
  setInitialized,
  setLastMessage,
  setTokenRefreshed,
  setBrowserSupport,
  setDeviceInfo,
  clearFCM,
} = fcmSlice.actions;

export default fcmSlice.reducer;
